import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Code,
  Zap,
  Shield,
  Smartphone,
  Search,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Check,
  X,
} from "lucide-react";
import { subscribeToNewsletter } from "./newsletterService";

export default function WebDevelopmentService() {
  const [activeTab, setActiveTab] = useState("frontend");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fallbackPalette = ["#0ea5e9", "#10b981", "#f97316", "#6366f1", "#ef4444", "#14b8a6"];

  const getInitials = (name) =>
    name
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const makeFallbackSvg = (label, color) => {
    const safeLabel = getInitials(label);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <defs>
          <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.9" />
            <stop offset="100%" stop-color="${color}" stop-opacity="0.7" />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="56" height="56" rx="14" fill="url(#g)" />
        <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#ffffff">
          ${safeLabel}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const technologies = {
    frontend: [
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg", darkLogo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-plain.svg" },
      { name: "Angular", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg" },
    ],
    backend: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg" },
      { name: "Ruby on Rails", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rails/rails-original.svg" },
      { name: ".NET", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dot-net/dot-net-original.svg" },
      { name: "Java", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" },
    ],
    cms: [
      { name: "WordPress", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/wordpress/wordpress-plain.svg" },
      { name: "Webflow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/webflow/webflow-original.svg" },
      { name: "Shopify", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/shopify/shopify-original.svg" },
      { name: "Contentful", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/contentful/contentful-original.svg" },
      { name: "Strapi", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/strapi/strapi-original.svg" },
      { name: "Sanity", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sanity/sanity-original.svg" },
    ],
  };

  const whatWeOffer = [
    {
      icon: <Code className="w-6 h-6" />,
      title: "Custom Web Applications",
      description:
        "Enterprise-grade web applications tailored to your specific business processes and workflows.",
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Responsive Websites",
      description:
        "Beautiful, mobile-first websites that provide seamless experiences across all devices.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "E-Commerce Solutions",
      description:
        "Full-featured online stores with secure payment processing and inventory management.",
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: "Progressive Web Apps",
      description:
        "Fast, reliable web applications that work offline and feel like native mobile apps.",
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "CMS Development",
      description:
        "Content management systems that make it easy to update and manage your website content.",
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Landing Pages",
      description:
        "High-converting landing pages optimized for marketing campaigns and lead generation.",
    },
  ];

  const whyChooseUs = [
    "Modern, scalable architecture built for growth",
    "SEO-optimized code for better search rankings",
    "Lightning-fast performance and load times",
    "Cross-browser compatibility guaranteed",
    "Security best practices implemented",
    "Ongoing support and maintenance",
    "Agile development methodology",
    "Regular progress updates and transparency",
  ];

  const packages = [
    {
      name: "Starter",
      price: "From R6,000",
      ideal: "Small businesses & startups",
      features: [
        { text: "Up to 5 pages", included: true },
        { text: "Responsive design", included: true },
        { text: "Basic SEO setup", included: true },
        { text: "Contact form", included: true },
        { text: "CMS integration", included: false },
        { text: "E-commerce functionality", included: false },
        { text: "Custom animations", included: false },
        { text: "Advanced integrations", included: false },
      ],
      popular: false,
    },
    {
      name: "Professional",
      price: "From R12,000",
      ideal: "Growing businesses",
      features: [
        { text: "Up to 15 pages", included: true },
        { text: "Responsive design", included: true },
        { text: "Advanced SEO", included: true },
        { text: "Contact & custom forms", included: true },
        { text: "CMS integration", included: true },
        { text: "Basic e-commerce", included: true },
        { text: "Custom animations", included: true },
        { text: "Advanced integrations", included: false },
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      price: "From R25,000",
      ideal: "Large organizations",
      features: [
        { text: "Unlimited pages", included: true },
        { text: "Responsive design", included: true },
        { text: "Enterprise SEO", included: true },
        { text: "Advanced forms & workflows", included: true },
        { text: "Headless CMS", included: true },
        { text: "Full e-commerce suite", included: true },
        { text: "Premium animations", included: true },
        { text: "Custom integrations", included: true },
      ],
      popular: false,
    },
  ];

  const process = [
    {
      step: "1",
      title: "Discovery & Strategy",
      description:
        "We start by understanding your business goals, target audience, and project requirements through detailed consultations.",
    },
    {
      step: "2",
      title: "Design & Prototyping",
      description:
        "Our designers create wireframes and interactive prototypes, allowing you to visualize the final product before development begins.",
    },
    {
      step: "3",
      title: "Development",
      description:
        "Our developers bring the designs to life using the latest technologies, writing clean, maintainable, and well-documented code.",
    },
    {
      step: "4",
      title: "Testing & QA",
      description:
        "Rigorous testing across multiple devices, browsers, and scenarios ensures your website works flawlessly for every user.",
    },
    {
      step: "5",
      title: "Launch & Optimization",
      description:
        "We deploy your website to production, monitor performance, and provide ongoing optimization and support.",
    },
  ];

  const showcaseImages = [
    {
      src: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=80",
      title: "Modern Product Dashboards",
      tag: "UI Engineering",
    },
    {
      src: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80",
      title: "Scalable Web Platforms",
      tag: "Architecture",
    },
    {
      src: "https://images.unsplash.com/photo-1487014679447-9f8336841d58?auto=format&fit=crop&w=1200&q=80",
      title: "Conversion-Focused Sites",
      tag: "Performance",
    },
  ];

  const startProject = () => {
    window.location.href = "/#contact";
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.newsletterEmail?.value || "";
    const result = subscribeToNewsletter(email);
    window.alert(result.message);
    if (result.ok) {
      form.reset();
    }
  };

  return (
    <div className="web-development-page">
      {/* Navigation Bar */}
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
        <div className="container navbar-container">
          <a href="/" className="navbar-brand">
            <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
          </a>

          <div className={`navbar-menu ${mobileMenuOpen ? "active" : ""}`}>
            <div className="services-dropdown">
              <div className="navbar-link active-link">Services</div>
              <div className="dropdown-menu">
                <Link to="/web-development" className="dropdown-item active-link">
                  <i className="fas fa-laptop-code"></i>
                  Web Development Services
                </Link>
                <Link to="/mobile-app" className="dropdown-item">
                  <i className="fas fa-mobile-alt"></i>
                  Mobile App Development
                </Link>
                <Link to="/custom-software" className="dropdown-item">
                  <i className="fas fa-cogs"></i>
                  Custom Software Development
                </Link>
                <Link to="/api-backend" className="dropdown-item">
                  <i className="fas fa-plug"></i>
                  API & Backend Services
                </Link>
                <Link to="/maintenance" className="dropdown-item">
                  <i className="fas fa-tools"></i>
                  Website & App Maintenance
                </Link>
                <Link to="/ai-services" className="dropdown-item">
                  <i className="fas fa-robot"></i>
                  AI & Machine Learning Services
                </Link>
              </div>
            </div>
            {/* <a href="/#work" className="navbar-link">Work</a> */}
            <a href="/#about" className="navbar-link">
              About
            </a>
{/*
            <a href="/#testimonials" className="navbar-link">
              Testimonials
            </a>
*/}
            <a href="/#faq" className="navbar-link">
              FAQ
            </a>
            <a href="/#contact" className="navbar-link">
              Contact
            </a>
            <Link to="/track-project" className="navbar-link">
              Track Project
            </Link>
            <button className="btn btn-primary nav-consult-btn" onClick={startProject}>
              Request Consultation
            </button>
          </div>

          <button
            className="navbar-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
          </button>
        </div>
      </nav>

      {/* Breadcrumb & Hero */}
      <section className="page-hero">
        <div className="container">
          <div className="breadcrumb">
            <a href="/">Home</a> / <a href="/#services">Services</a> /{" "}
            <span className="current">Web Development</span>
          </div>
          <h1 className="page-title">Web Development Services in Johannesburg</h1>
          <p className="hero-description">
            We provide web design Johannesburg brands rely on, website
            development South African companies scale with, and custom web
            development teams use to convert more customers. Need a{" "}
            <a href="/#contact">web developer in Johannesburg</a>? Let&apos;s
            talk.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="section">
        <div className="container">
          <div className="overview-grid">
            <div className="overview-content">
              <h2 className="section-title-left">
                Web Platforms Built for Business Impact
              </h2>
              <p className="overview-text">
                Your web platform is often a core operational and customer
                touchpoint. We design and deliver websites and web applications
                that prioritize reliability, clarity, and measurable results
                for businesses serving Johannesburg and South Africa.
              </p>
              <p className="overview-text">
                Whether you need a modern marketing site, a complex web
                application, or ecommerce website development South African
                retailers trust, our team delivers production-ready systems
                using proven technologies and strong engineering standards.
              </p>
            </div>
            <div className="benefits-card">
              <h3 className="benefits-title">Key Benefits</h3>
              <div className="benefits-list">
                {whyChooseUs.map((benefit, idx) => (
                  <div key={idx} className="benefit-item">
                    <CheckCircle className="benefit-icon" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="section bg-light">
        <div className="container">
          <h2 className="section-title-center">What High-Quality Build Looks Like</h2>
          <p className="section-subtitle">
            Clean interfaces, stable architecture, and polished user flows across
            every screen size.
          </p>
          <div className="showcase-grid">
            {showcaseImages.map((image, idx) => (
              <article key={idx} className="showcase-card">
                <img src={image.src} alt={image.title} className="showcase-image" />
                <div className="showcase-overlay">
                  <span className="showcase-tag">{image.tag}</span>
                  <h3 className="showcase-title">{image.title}</h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section bg-light">
        <div className="container">
          <h2 className="section-title-center">What We Offer</h2>
          <p className="section-subtitle">
            Enterprise-grade web delivery services aligned to your business
            requirements
          </p>
          <div className="services-offer-grid">
            {whatWeOffer.map((item, idx) => (
              <div key={idx} className="service-offer-card">
                <div className="service-offer-icon">{item.icon}</div>
                <h3 className="service-offer-title">{item.title}</h3>
                <p className="service-offer-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="section">
        <div className="container">
          <h2 className="section-title-center">Technologies We Use</h2>
          <p className="section-subtitle">
            We use proven technologies to deliver secure, maintainable, and
            robust systems
          </p>

          <div className="tech-tabs">
            {Object.keys(technologies).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`tech-tab ${activeTab === tab ? "active" : ""}`}
              >
                {tab === "cms"
                  ? "CMS Platforms"
                  : `${
                      tab.charAt(0).toUpperCase() + tab.slice(1)
                    } Technologies`}
              </button>
            ))}
          </div>

          <div className="tech-grid">
            {technologies[activeTab].map((tech, idx) => (
              <div key={idx} className="tech-item">
                <div className="tech-logo-container">
                  <img
                    src={tech.logo}
                    alt={`${tech.name} logo`}
                    className="tech-logo"
                    onError={(e) => {
                      e.target.onerror = null;
                      const color =
                        fallbackPalette[
                          Math.abs(tech.name.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) %
                            fallbackPalette.length
                        ];
                      e.target.src = makeFallbackSvg(tech.name, color);
                    }}
                  />
                </div>
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section bg-light">
        <div className="container">
          <h2 className="section-title-center">Our Development Process</h2>
          <p className="section-subtitle">
            A transparent, collaborative approach that keeps you involved every
            step of the way
          </p>
          <div className="process-grid">
            {process.map((item, idx) => (
              <div key={idx} className="process-step">
                <div className="process-step-card">
                  <div className="process-step-number">{item.step}</div>
                  <h3 className="process-step-title">{item.title}</h3>
                  <p className="process-step-description">{item.description}</p>
                </div>
                {idx < process.length - 1 && (
                  <div className="process-arrow">
                    <ArrowRight className="arrow-icon" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section">
        <div className="container">
          <h2 className="section-title-center">Choose Your Package</h2>
          <p className="section-subtitle">
            Engagement options aligned to project scope, complexity, and
            delivery goals
          </p>
          <div className="packages-grid">
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                className={`package-card ${pkg.popular ? "popular" : ""}`}
              >
                {pkg.popular && (
                  <div className="popular-badge">MOST POPULAR</div>
                )}
                <h3 className="package-name">{pkg.name}</h3>
                <div className="package-price">{pkg.price}</div>
                <p className="package-ideal">{pkg.ideal}</p>

                <div className="package-features">
                  {pkg.features.map((feature, i) => (
                    <div key={i} className="package-feature">
                      {feature.included ? (
                        <Check className="feature-icon included" />
                      ) : (
                        <X className="feature-icon excluded" />
                      )}
                      <span
                        className={`feature-text ${
                          feature.included ? "" : "excluded"
                        }`}
                      >
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  className={`btn package-btn ${
                    pkg.popular ? "btn-primary" : "btn-secondary"
                  }`}
                  onClick={startProject}
                >
                  Discuss Requirements
                </button>
              </div>
            ))}
          </div>
          <p className="package-note">
            All packages include a structured discovery session and 30 days of
            post-launch support
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">Plan Your Next Web Initiative</h2>
          <p className="cta-description">
            Discuss your roadmap with our team and get a clear delivery plan
            tailored to your business objectives.
          </p>
          <div className="cta-buttons">
            <button className="btn cta-btn-primary" onClick={startProject}>
              Request Consultation
            </button>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-decoration"></div>
        <div className="footer-decoration"></div>

        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
              </div>
              <p className="footer-about">
                We design, build, and support secure digital systems for
                organizations that require reliability, scalability, and strong
                delivery governance.
              </p>
              <div className="footer-social">
                <a
                  href="https://www.linkedin.com/company/techxm/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a
                  href="https://www.instagram.com/techxm_za/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.facebook.com/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-facebook"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>
                  <a href="/web-development">
                    <i className="fas fa-chevron-right"></i>
                    Web Development
                  </a>
                </li>
                <li>
                  <a href="/mobile-app">
                    <i className="fas fa-chevron-right"></i>
                    Mobile Apps
                  </a>
                </li>
                <li>
                  <a href="/custom-software">
                    <i className="fas fa-chevron-right"></i>
                    Custom Software
                  </a>
                </li>
                <li>
                  <a href="/api-backend">
                    <i className="fas fa-chevron-right"></i>
                    API & Backend
                  </a>
                </li>
                <li>
                  <a href="/maintenance">
                    <i className="fas fa-chevron-right"></i>
                    Maintenance
                  </a>
                </li>
                <li>
                  <a href="/ai-services">
                    <i className="fas fa-chevron-right"></i>
                    AI & Machine Learning
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="/#about">
                    <i className="fas fa-chevron-right"></i>
                    About
                  </a>
                </li>
                <li>
                  <a href="/#faq">
                    <i className="fas fa-chevron-right"></i>
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="/track-project">
                    <i className="fas fa-chevron-right"></i>
                    Track Project
                  </a>
                </li>
                <li>
{/*
                  <a href="/#testimonials">
                    <i className="fas fa-chevron-right"></i>
                    Testimonials
                  </a>
*/}
                </li>
                <li>
                  <a href="/#contact">
                    <i className="fas fa-chevron-right"></i>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact Info</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Ohenimuri, Walkerville</span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:0765536048">076 553 6048</a>
                  <span> / </span>
                  <a href="tel:0742686212">074 268 6212</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@techxm.co.za">info@techxm.co.za</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <span>Mon - Fri: 9:00 - 18:00</span>
                </div>
              </div>

              <div className="footer-newsletter">
                <p className="newsletter-text">Subscribe to our newsletter</p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="newsletter-input"
                    placeholder="Your email address"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; 2025 TECHXM. All rights reserved. Enterprise Digital
              Solutions.
            </p>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
              <a href="/cookie-policy">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        /* Page-specific styles */
        .web-development-page {
          font-family: var(
            --font-body,
            "Manrope",
            "Segoe UI",
            system-ui,
            -apple-system,
            sans-serif
          );
          color: var(--text);
        }

        .web-development-page h1,
        .web-development-page h2,
        .web-development-page h3,
        .web-development-page h4,
        .web-development-page h5,
        .web-development-page h6 {
          font-family: var(
            --font-display,
            "Sora",
            "Segoe UI",
            system-ui,
            -apple-system,
            sans-serif
          );
        }

        /* Navigation Bar */
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          z-index: 1000;
          transition: all 0.3s ease;
          padding: 1rem 0;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }

        .navbar.scrolled {
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          padding: 0.75rem 0;
        }

        .navbar-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 1.5rem;
        }

        .navbar-brand {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 700;
          font-size: 1.5rem;
          color: var(--primary);
          text-decoration: none;
        }

        .navbar-brand .brand-logo-mark {
          width: 150px;
          height: 90px;
          display: block;
          object-fit: contain;
        }

        .navbar-menu {
          display: flex;
          align-items: center;
          gap: 2rem;
        }

        .navbar-link {
          color: var(--text);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s ease;
          cursor: pointer;
        }

        .navbar-link:hover {
          color: var(--primary);
        }

        .navbar-link.active-link {
          color: var(--primary);
          font-weight: 600;
        }

        .navbar-toggle {
          display: none;
          background: none;
          border: none;
          font-size: 1.5rem;
          color: var(--text);
          cursor: pointer;
        }

        /* Services Dropdown */
        .services-dropdown {
          position: relative;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: -2rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          padding: 1rem 0;
          min-width: 250px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
          transition: all 0.3s ease;
          z-index: 100;
        }

        .services-dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          color: var(--text);
          text-decoration: none;
          transition: background 0.3s ease;
        }

        .dropdown-item:hover {
          background: rgba(0, 128, 128, 0.1);
          color: var(--primary);
        }

        .dropdown-item.active-link {
          background: rgba(0, 128, 128, 0.1);
          color: var(--primary);
          font-weight: 600;
        }

        .dropdown-item i {
          width: 20px;
        }

        /* Hero Section */
        .page-hero {
          background: linear-gradient(
            135deg,
            var(--primary-dark) 0%,
            var(--secondary) 100%
          );
          color: white;
          padding: 8rem 0 6rem;
          position: relative;
          overflow: hidden;
          margin-top: 60px; /* Account for fixed navbar */
        }

        .page-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
              circle at 20% 80%,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 255, 255, 0.05) 0%,
              transparent 50%
            );
        }

        .breadcrumb {
          font-size: 0.9rem;
          margin-bottom: 2rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .breadcrumb a {
          color: rgba(255, 255, 255, 0.9);
          text-decoration: none;
          transition: var(--transition);
        }

        .breadcrumb a:hover {
          color: white;
          text-decoration: underline;
        }

        .breadcrumb .current {
          color: white;
          font-weight: 600;
        }

        .page-title {
          font-size: 3.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          line-height: 1.2;
          letter-spacing: -0.02em;
        }

        .hero-description {
          font-size: 1.25rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
        }

        /* Technologies Section with Logos */
        .tech-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }

        @media (min-width: 640px) {
          .tech-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .tech-grid {
            grid-template-columns: repeat(6, 1fr);
          }
        }

        .tech-item {
          background: white;
          padding: 1.6rem 1rem;
          border-radius: 16px;
          text-align: center;
          font-weight: 600;
          color: var(--text);
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
          transition: var(--transition);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.85rem;
          position: relative;
          overflow: hidden;
        }

        .tech-item:hover {
          border-color: rgba(0, 128, 128, 0.35);
          color: var(--primary);
          transform: translateY(-4px);
          box-shadow: 0 18px 30px rgba(15, 23, 42, 0.12);
        }

        .tech-logo-container {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(145deg, #ffffff, #f1f5f9);
          border-radius: 16px;
          padding: 12px;
          border: 1px solid rgba(148, 163, 184, 0.35);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85),
            0 10px 18px rgba(15, 23, 42, 0.12);
        }

        .tech-logo {
          width: 100%;
          height: 100%;
          object-fit: contain;
          filter: drop-shadow(0 6px 10px rgba(15, 23, 42, 0.15));
          transition: transform 0.3s ease, filter 0.3s ease;
        }

        .tech-item:hover .tech-logo {
          transform: scale(1.06);
          filter: drop-shadow(0 10px 16px rgba(0, 128, 128, 0.2));
        }

        .tech-name {
          font-size: 0.9rem;
          font-weight: 500;
        }

        /* Responsive Navigation */
        @media (max-width: 1024px) {
          .navbar-toggle {
            display: block;
          }

          .navbar-menu {
            position: fixed;
            top: 70px;
            left: 0;
            width: 100%;
            background: white;
            flex-direction: column;
            padding: 2rem;
            gap: 1.5rem;
            transform: translateY(-100%);
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
            z-index: 999;
          }

          .navbar-menu.active {
            transform: translateY(0);
            opacity: 1;
            visibility: visible;
          }

          .services-dropdown .dropdown-menu {
            position: static;
            opacity: 1;
            visibility: visible;
            transform: none;
            box-shadow: none;
            background: rgba(0, 128, 128, 0.05);
            margin-top: 1rem;
            border-radius: 8px;
          }
        }

        /* Overview Section */
        .overview-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: start;
        }

        @media (min-width: 768px) {
          .overview-grid {
            grid-template-columns: 1fr 1fr;
            gap: 4rem;
          }
        }

        .section-title-left {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: var(--text);
          line-height: 1.2;
        }

        .overview-text {
          color: var(--text-light);
          margin-bottom: 1.5rem;
          line-height: 1.6;
          font-size: 1.1rem;
        }

        .benefits-card {
          background: linear-gradient(
            135deg,
            var(--bg-light) 0%,
            rgba(0, 128, 128, 0.05) 100%
          );
          padding: 2.5rem;
          border-radius: var(--radius-lg);
          border: 1px solid var(--border);
          box-shadow: var(--shadow);
        }

        .benefits-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          color: var(--text);
        }

        .benefits-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .benefit-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          color: var(--text);
        }

        .benefit-icon {
          width: 1.25rem;
          height: 1.25rem;
          color: var(--primary);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        /* Showcase Section */
        .showcase-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }

        @media (min-width: 768px) {
          .showcase-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .showcase-card {
          position: relative;
          border-radius: var(--radius-lg);
          overflow: hidden;
          min-height: 300px;
          border: 1px solid var(--border);
          box-shadow: var(--shadow);
          transition: var(--transition);
        }

        .showcase-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--shadow-lg);
        }

        .showcase-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .showcase-card:hover .showcase-image {
          transform: scale(1.05);
        }

        .showcase-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.15));
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 1.25rem;
          color: white;
        }

        .showcase-tag {
          display: inline-block;
          width: fit-content;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 999px;
          padding: 0.2rem 0.7rem;
          font-size: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .showcase-title {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 600;
        }

        /* What We Offer Section */
        .services-offer-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }

        @media (min-width: 640px) {
          .services-offer-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .services-offer-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .service-offer-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          padding: 2rem;
          border-radius: var(--radius-lg);
          transition: var(--transition);
          height: 100%;
        }

        .service-offer-card:hover {
          transform: translateY(-5px);
          border-color: var(--primary);
          box-shadow: var(--shadow-lg);
        }

        .service-offer-icon {
          background: linear-gradient(
            135deg,
            var(--primary) 0%,
            var(--secondary) 100%
          );
          color: white;
          width: 3rem;
          height: 3rem;
          border-radius: var(--radius);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .service-offer-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: var(--text);
        }

        .service-offer-description {
          color: var(--text-light);
          line-height: 1.6;
          font-size: 0.95rem;
        }

        /* Technologies Tabs */
        .tech-tabs {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .tech-tab {
          padding: 0.75rem 1.5rem;
          border-radius: var(--radius);
          font-weight: 600;
          font-size: 0.9rem;
          background: var(--bg-light);
          color: var(--text);
          border: 1px solid var(--border);
          cursor: pointer;
          transition: var(--transition);
        }

        .tech-tab:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        .tech-tab.active {
          background: linear-gradient(
            135deg,
            var(--primary) 0%,
            var(--primary-dark) 100%
          );
          color: white;
          border-color: var(--primary);
        }

        /* Process Section */
        .process-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          position: relative;
        }

        @media (min-width: 768px) {
          .process-grid {
            grid-template-columns: repeat(5, 1fr);
          }
        }

        .process-step {
          position: relative;
          z-index: 1;
        }

        .process-step-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          padding: 1.5rem;
          border-radius: var(--radius-lg);
          height: 100%;
          position: relative;
          transition: var(--transition);
        }

        .process-step-card:hover {
          border-color: var(--primary);
          box-shadow: var(--shadow);
        }

        .process-step-number {
          background: linear-gradient(
            135deg,
            var(--primary) 0%,
            var(--secondary) 100%
          );
          color: white;
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.25rem;
          margin-bottom: 1rem;
        }

        .process-step-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: var(--text);
        }

        .process-step-description {
          color: var(--text-light);
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .process-arrow {
          display: none;
          position: absolute;
          top: 1.25rem;
          right: -1.5rem;
          z-index: 2;
        }

        @media (min-width: 768px) {
          .process-arrow {
            display: block;
          }
        }

        .arrow-icon {
          width: 1.5rem;
          height: 1.5rem;
          color: var(--primary-light);
        }

        /* Packages Section */
        .packages-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        @media (min-width: 768px) {
          .packages-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .package-card {
          background: var(--bg-card);
          border: 2px solid var(--border);
          border-radius: var(--radius-lg);
          padding: 2.5rem;
          position: relative;
          transition: var(--transition);
          display: flex;
          flex-direction: column;
        }

        .package-card:hover {
          transform: translateY(-5px);
          box-shadow: var(--shadow-lg);
        }

        .package-card.popular {
          border-color: var(--primary);
          background: linear-gradient(
            135deg,
            var(--primary-dark) 0%,
            var(--secondary) 100%
          );
          color: white;
          transform: scale(1.05);
        }

        .popular-badge {
          background: var(--accent);
          color: white;
          font-size: 0.75rem;
          font-weight: 700;
          padding: 0.25rem 1rem;
          border-radius: 20px;
          position: absolute;
          top: -0.75rem;
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
        }

        .package-name {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .package-card.popular .package-name {
          color: white;
        }

        .package-price {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: var(--primary);
        }

        .package-card.popular .package-price {
          color: white;
        }

        .package-ideal {
          color: var(--text-light);
          margin-bottom: 2rem;
          font-size: 0.9rem;
        }

        .package-card.popular .package-ideal {
          color: rgba(255, 255, 255, 0.9);
        }

        .package-features {
          flex-grow: 1;
          margin-bottom: 2rem;
        }

        .package-feature {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .feature-icon {
          width: 1.25rem;
          height: 1.25rem;
          flex-shrink: 0;
        }

        .feature-icon.included {
          color: var(--primary);
        }

        .package-card.popular .feature-icon.included {
          color: white;
        }

        .feature-icon.excluded {
          color: var(--border);
        }

        .package-card.popular .feature-icon.excluded {
          color: rgba(255, 255, 255, 0.5);
        }

        .feature-text {
          font-size: 0.9rem;
          color: var(--text);
        }

        .package-card.popular .feature-text {
          color: white;
        }

        .feature-text.excluded {
          color: var(--text-lighter);
        }

        .package-card.popular .feature-text.excluded {
          color: rgba(255, 255, 255, 0.6);
        }

        .package-btn {
          width: 100%;
        }

        .package-note {
          text-align: center;
          color: var(--text-lighter);
          font-size: 0.9rem;
        }

        /* CTA Section */
        .cta-section {
          background: linear-gradient(
            135deg,
            var(--primary) 0%,
            var(--secondary) 100%
          );
          color: white;
          padding: 5rem 0;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .cta-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
              circle at 20% 80%,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 255, 255, 0.05) 0%,
              transparent 50%
            );
        }

        .cta-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          position: relative;
          z-index: 2;
        }

        .cta-description {
          font-size: 1.25rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 2.5rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          position: relative;
          z-index: 2;
          line-height: 1.6;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
          position: relative;
          z-index: 2;
        }

        .cta-btn-primary {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: white;
          color: var(--primary);
        }

        .cta-btn-primary:hover {
          background: var(--bg-light);
          transform: translateY(-2px);
        }

        .cta-btn-secondary {
          border: 2px solid white;
          color: white;
          background: transparent;
        }

        .cta-btn-secondary:hover {
          background: white;
          color: var(--primary);
        }

        .btn-icon {
          width: 1.25rem;
          height: 1.25rem;
        }

        /* Utility Classes */
        .section {
          padding: 5rem 0;
        }

        .bg-light {
          background-color: var(--bg-light);
        }

        .container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .section-title-center {
          text-align: center;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--text);
        }

        .section-subtitle {
          text-align: center;
          color: var(--text-light);
          font-size: 1.1rem;
          max-width: 600px;
          margin: 0 auto 3rem;
          line-height: 1.6;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          border-radius: var(--radius);
          font-weight: 600;
          transition: var(--transition);
          font-size: 0.95rem;
          border: none;
          cursor: pointer;
          text-decoration: none;
        }

        .btn-primary {
          background: linear-gradient(
            135deg,
            var(--primary),
            var(--primary-dark)
          );
          color: white;
          position: relative;
          overflow: hidden;
        }

        .btn-primary::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          transition: left 0.7s ease;
        }

        .btn-primary:hover::before {
          left: 100%;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .nav-consult-btn {
          padding: 0.6rem 1.2rem;
          font-size: 0.85rem;
        }

        .btn-secondary {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text);
          position: relative;
          overflow: hidden;
        }

        .btn-secondary::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: var(--primary);
          opacity: 0.1;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }

        .btn-secondary:hover::before {
          transform: scaleX(1);
        }

        .btn-secondary:hover {
          border-color: var(--primary);
          color: var(--text);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .page-title {
            font-size: 2.5rem;
          }

          .section-title-center,
          .section-title-left {
            font-size: 2rem;
          }

          .cta-title {
            font-size: 2rem;
          }

          .cta-description {
            font-size: 1.1rem;
          }

          .package-card.popular {
            transform: none;
          }

          .process-arrow {
            display: none;
          }

          .navbar {
            padding: 0.75rem 0;
          }
          
          .page-hero {
            padding: 6rem 0 4rem;
            margin-top: 60px;
          }
        }
      `}</style>
    </div>
  );
}








